import { Placement } from '@iheartjane/dm-sdk';
import { useCallback, useMemo } from 'react';

import type { MenuRow } from '@jane/business-admin/types';
import { useFetchCustomMultiPlacements } from '@jane/dm/internal';

import { useMenuRowType } from './useMenuRowType';

const getPlacementForRow = (rowType: string | null | undefined) => {
  switch (rowType) {
    case 'best_selling':
      return { placement: Placement.BestSelling } as const;
    case 'sale':
      return { placement: Placement.Sale } as const;
    default:
      return null;
  }
};

export const useMenuRowSdkProducts = (
  menuRows: MenuRow[],
  storeId: string,
  enabled: boolean
) => {
  const { getRowType } = useMenuRowType();

  const sdkRows = useMemo(
    () =>
      menuRows.reduce<
        {
          key: string;
          placement: NonNullable<ReturnType<typeof getPlacementForRow>>;
        }[]
      >((acc, row) => {
        const rowType = getRowType(row);
        const placement = getPlacementForRow(rowType);
        if (!placement) return acc;

        const key = row.id !== undefined ? String(row.id) : rowType!;
        acc.push({ key, placement });
        return acc;
      }, []),
    [menuRows, getRowType]
  );

  const placements = useMemo(
    () => sdkRows.map((row) => row.placement),
    [sdkRows]
  );
  const rowKeys = useMemo(() => sdkRows.map((row) => row.key), [sdkRows]);

  const { placementViewModels, isLoading } = useFetchCustomMultiPlacements({
    enabled: enabled && placements.length > 0,
    placements,
    storeId: Number(storeId),
  });

  const getProductsForRow = useCallback(
    (rowKey: string) => {
      const index = rowKeys.indexOf(rowKey);
      if (index === -1) return undefined;

      const viewModel = placementViewModels[index];
      if (!viewModel?.isPlacement || !viewModel.placement) return undefined;

      return viewModel.placement.products;
    },
    [placementViewModels, rowKeys]
  );

  return { isLoading, getProductsForRow };
};
